import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, STATUS } from "@/theme/tokens";
import { MetaChip } from "@/ui/Badges/MetaChip";
import { PUNCH_SUMMARY_CATEGORY_DOT_HEX } from "@/utils/punchSummary";

export type ShiftDeltaValuesProps = {
	scheduledHours: number;
	actualHours: number;
	diffHours: number;
};

const formatHours = (hours: number): string => `${hours.toFixed(1)}h`;
const formatDiff = (diffHours: number): string => `${diffHours > 0 ? "+" : ""}${formatHours(diffHours)}`;

/**
 * The gap itself, as a chip on the row's name line — the one number a manager scans a Shift Delta
 * list for. The bare figure doesn't say what it measures, so the full labelled breakdown stays on
 * the chip's tooltip, which is also the row's accessible description.
 */
export const ShiftDeltaChip: FC<ShiftDeltaValuesProps> = ({ scheduledHours, actualHours, diffHours }) => {
	const { t } = useTranslation();

	const tooltipTitle = t("punchSummary.shiftDelta.tooltip", {
		scheduled: scheduledHours.toFixed(1),
		actual: actualHours.toFixed(1),
		diff: diffHours.toFixed(1),
	});

	return <MetaChip label={formatDiff(diffHours)} accentColor={PUNCH_SUMMARY_CATEGORY_DOT_HEX.shiftDelta} tooltip={tooltipTitle} sx={{ flexShrink: 0 }} />;
};

/**
 * Scheduled vs. actual hours as the row's sub-line.
 *
 * The word labels used to hide themselves below a 186px row width (via a container query), because
 * the panel was sized to the day column it opened from — which at laptop widths left three
 * unlabelled numbers whose meaning existed only in a tooltip. The panel owns its width now, so the
 * labels are simply always there.
 */
export const ShiftDeltaValues: FC<ShiftDeltaValuesProps> = ({ scheduledHours, actualHours }) => {
	const { t } = useTranslation();

	return (
		<Box component="span" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY }}>
			{/* Each label+number is one unbreakable phrase, so a wrap drops the whole group to the next
			 * line instead of orphaning a number from the word that gives it meaning. */}
			<Box component="span" sx={{ whiteSpace: "nowrap" }}>
				{t("punchSummary.shiftDelta.scheduled")} {formatHours(scheduledHours)}
			</Box>
			<Box component="span" sx={{ color: INK.DISABLED }}>
				·
			</Box>
			<Box component="span" sx={{ whiteSpace: "nowrap", color: STATUS.INFO.main, fontWeight: 500 }}>
				{t("punchSummary.shiftDelta.actual")} {formatHours(actualHours)}
			</Box>
		</Box>
	);
};
